import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Search, Trash2, X } from "lucide-react";
import {
  MEDIA_ORIENTATIONS,
  MediaFolderSchema,
  MediaListQuerySchema,
  type MediaOrientation,
} from "@ckgo2/shared";
import { MEDIA_FOLDERS, type MediaFolder } from "@ckgo2/shared/types";
import i18n from "@/i18n";
import DestinationPicker from "@/components/common/DestinationPicker";
import { mediaFolderLabel } from "@/lib/mediaFormat";
import type { MediaFilterKey, MediaLibraryFilters } from "@/hooks/useMediaLibrary";

/**
 * Lišta filtrů stránky Média (vize podnety-tymu-2026-10, F7.5) a filtr druhu pro levý
 * sloupec. Hodnoty drží adresa (`useMediaLibrary`); lišta jen hlásí změny přes `onChange`.
 *
 * Země: sdílený `DestinationPicker` jen se zemskou úrovní a bez volby „Bez destinace“;
 * „Bez země“ (`country=none`) je samostatné tlačítko vedle a × vedle výběru zemi zruší
 * (výběr v režimu jedné hodnoty bez „Bez destinace“ hodnotu smazat neumí).
 */

const SEARCH_DEBOUNCE_MS = 300;

type MediaUsageFilter = NonNullable<MediaLibraryFilters["usage"]>;
/** Volby filtru použití ze sdíleného schématu (`MediaListQuerySchema`), ne z ruční kopie. */
const USAGE_OPTIONS: readonly MediaUsageFilter[] =
  MediaListQuerySchema.shape.usage.unwrap().options;

/**
 * Popisek orientace fotky. JEDINÁ mapa orientací v adminu: čte ji filtr i prohlížeč fotek
 * (`PhotoViewer`), ať se popisky téže orientace nerozejdou.
 */
export function mediaOrientationLabel(orientation: MediaOrientation): string {
  const labels: Record<MediaOrientation, string> = {
    landscape: i18n.t("media.filters.orientationLandscape"),
    portrait: i18n.t("media.filters.orientationPortrait"),
    square: i18n.t("media.filters.orientationSquare"),
  };
  return labels[orientation];
}

/**
 * Text pole hledání po změně `q` v adrese. Vlastní debounce pošle do adresy oříznutý text,
 * takže pole se přepíše jen tehdy, když se od adresy liší i po oříznutí (změna zvenku:
 * „Zrušit filtry“, odkaz); jinak by zmizela mezera na konci a „Ha “ + „Long“ dalo „HaLong“.
 */
export function searchFieldAfterUrlChange(search: string, urlQ: string): string {
  return search.trim() === urlQ ? search : urlQ;
}

const SELECT_CLASS =
  "rounded-lg border border-gray-300 px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800";
const toggleClass = (on: boolean) =>
  `inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
    on
      ? "border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
      : "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
  }`;

export interface MediaFiltersProps {
  filters: MediaLibraryFilters;
  onChange: (key: MediaFilterKey, value: string | null) => void;
  onClear: () => void;
  /** Počet aktivních filtrů (`activeMediaFilterCount`). */
  activeCount: number;
  /** „Zrušit filtry“ v liště; stránka ho vypne v prázdném stavu, kde tlačítko nese mřížka. */
  showClear: boolean;
  /** Dřívější zdroje (`useMediaSources`), česky abecedně z API. */
  sources: readonly string[];
  /**
   * Bez přepínače koše (okno výběru fotek: smazaná fotka do galerie nepatří). Stránka Média
   * prop nepředává, koš tam zůstává.
   */
  hideTrash?: boolean;
}

export default function MediaFilters({
  filters,
  onChange,
  onClear,
  activeCount,
  showClear,
  sources,
  hideTrash = false,
}: MediaFiltersProps): ReactElement {
  const { t } = useTranslation();

  // Text hledání jde do adresy až po pauze v psaní. Když se `q` v adrese změní zvenku
  // („Zrušit filtry“, odkaz), pole se srovná s adresou; jinak by debounce starý text
  // do adresy vrátil.
  const urlQ = filters.q ?? "";
  const [search, setSearch] = useState(urlQ);
  const [syncedQ, setSyncedQ] = useState(urlQ);
  if (syncedQ !== urlQ) {
    setSyncedQ(urlQ);
    setSearch(searchFieldAfterUrlChange(search, urlQ));
  }
  useEffect(() => {
    const next = search.trim();
    if (next === urlQ) return;
    const timer = setTimeout(() => onChange("q", next || null), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search, urlQ, onChange]);

  const usageLabels: Record<MediaUsageFilter, string> = {
    used: t("media.filters.usageUsed"),
    unused: t("media.filters.usageUnused"),
  };

  const countryNone = filters.country === "none";
  const countryId = filters.country && !countryNone ? filters.country : null;
  // Aktivní zdroj, který v nabídce není (smazaný, z odkazu), se přidá, ať select
  // neukazuje „Všechny zdroje“ při aktivním filtru.
  const sourceOptions =
    filters.source && !sources.includes(filters.source) ? [filters.source, ...sources] : sources;

  return (
    <div
      data-media-filters=""
      role="group"
      aria-label={t("media.filters.label")}
      className="flex flex-wrap items-center gap-2"
    >
      <div className="relative min-w-[200px] flex-1">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
        <input
          type="search"
          data-media-filter="q"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("media.filters.search")}
          aria-label={t("media.filters.search")}
          className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-3 text-sm dark:border-gray-600 dark:bg-gray-800"
        />
      </div>

      <div data-media-filter="country" className="flex min-w-[220px] items-center gap-1">
        <div className="flex-1">
          <DestinationPicker
            mode="single"
            level="country"
            allowNone={false}
            value={countryId}
            onChange={(id) => onChange("country", id)}
          />
        </div>
        {countryId && (
          <button
            type="button"
            onClick={() => onChange("country", null)}
            aria-label={t("media.filters.countryClear")}
            title={t("media.filters.countryClear")}
            className="btn-ghost p-2"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <button
        type="button"
        data-media-filter-country-none=""
        aria-pressed={countryNone}
        onClick={() => onChange("country", countryNone ? null : "none")}
        className={toggleClass(countryNone)}
      >
        {t("media.filters.countryNone")}
      </button>

      <button
        type="button"
        data-media-filter="starred"
        aria-pressed={!!filters.starred}
        onClick={() => onChange("starred", filters.starred ? null : "1")}
        className={toggleClass(!!filters.starred)}
      >
        {t("media.filters.starred")}
      </button>

      <select
        data-media-filter="source"
        value={filters.source ?? ""}
        onChange={(e) => onChange("source", e.target.value || null)}
        aria-label={t("media.filters.sourceAll")}
        className={SELECT_CLASS}
      >
        <option value="">{t("media.filters.sourceAll")}</option>
        {sourceOptions.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      <select
        data-media-filter="orientation"
        value={filters.orientation ?? ""}
        onChange={(e) => onChange("orientation", e.target.value || null)}
        aria-label={t("media.filters.orientationAll")}
        className={SELECT_CLASS}
      >
        <option value="">{t("media.filters.orientationAll")}</option>
        {MEDIA_ORIENTATIONS.map((o) => (
          <option key={o} value={o}>
            {mediaOrientationLabel(o)}
          </option>
        ))}
      </select>

      <select
        data-media-filter="usage"
        value={filters.usage ?? ""}
        onChange={(e) => onChange("usage", e.target.value || null)}
        aria-label={t("media.filters.usageAll")}
        className={SELECT_CLASS}
      >
        <option value="">{t("media.filters.usageAll")}</option>
        {USAGE_OPTIONS.map((u) => (
          <option key={u} value={u}>
            {usageLabels[u]}
          </option>
        ))}
      </select>

      {!hideTrash && (
        <button
          type="button"
          data-media-filter="trash"
          aria-pressed={!!filters.trash}
          onClick={() => onChange("trash", filters.trash ? null : "1")}
          className={toggleClass(!!filters.trash)}
        >
          <Trash2 className="h-4 w-4" /> {t("media.filters.trash")}
        </button>
      )}

      {showClear && (
        <button
          type="button"
          data-media-clear-filters=""
          onClick={onClear}
          className="btn-ghost text-sm"
        >
          <X className="h-4 w-4" /> {t("media.page.clearFilters")}
          <span className="rounded-full bg-gray-200 px-1.5 text-xs dark:bg-gray-700">
            {activeCount}
          </span>
        </button>
      )}
    </div>
  );
}

/** Filtr druhu (pevná kategorie `media.folder`) v levém sloupci pod stromem alb. */
export function MediaFolderFilter({
  value,
  onChange,
}: {
  value: MediaFolder | undefined;
  onChange: (value: MediaFolder | null) => void;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <select
      data-media-filter="folder"
      value={value ?? ""}
      onChange={(e) => onChange(MediaFolderSchema.safeParse(e.target.value).data ?? null)}
      aria-label={t("media.filters.folderAll")}
      className={`${SELECT_CLASS} w-full`}
    >
      <option value="">{t("media.filters.folderAll")}</option>
      {MEDIA_FOLDERS.map((f) => (
        <option key={f} value={f}>
          {mediaFolderLabel(f)}
        </option>
      ))}
    </select>
  );
}
